import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';

import { formatCurrency, getCurrencyTypeLabel } from 'utils/currencies';

import { useNavigate } from '@tanstack/react-router';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import { useProductsGraphs } from 'hooks/products/useGraphs';
import { ProductsWithHoldingsAndRates } from 'hooks/products/useProducts';
import useAppLanguage from 'hooks/useAppLanguage';
import { orderBy } from 'utils/sorting';
import NoProductsFound from './NoProductsFound';
import {
  ListContainer,
  ProductListHeader,
  ProductListItem,
  Typography,
} from '@altify/components';
import { TimeFrameKey } from 'data/time-frames';
import { ExploreActionButton } from './ExploreActionButton';
import { ExploreGainLoss } from './ExploreGainLoss';
import { useContent } from '@altify/utils';
import { exploreProductsContent } from '../explore.content';
import { clsx } from 'clsx';
import { GradientOrColorView } from 'components/GradientOrColorView/GradientOrColorView';
import { getColorOrGradient } from 'utils/getColorOrGradient';

const ProductsTable = ({
  products,
  displayCurrency,
  timeFrameKey,
  isLoading,
  isSearching,
  isMobile,
}: {
  products: ProductsWithHoldingsAndRates;
  displayCurrency: string;
  timeFrameKey: TimeFrameKey;
  isLoading?: boolean;
  isSearching?: boolean;
  isMobile: boolean;
}) => {
  const navigate = useNavigate();
  const language = useAppLanguage();
  const localizedContent = useContent(exploreProductsContent);
  const productGraph = useProductsGraphs(timeFrameKey, {displayCurrency: displayCurrency});
  
  const combinedData =
    products.sort(orderBy('displayOrder', 'asc')).map(p => {
      return {
        code: p.code,
        title: p.name?.replace('Bundle', ''),
        tag: p.tags?.[0],
        subtitle: getCurrencyTypeLabel(p.code, p.type, language),
        isTracked: p.isTracked,
        balanceText: {
          primary: p.isTracked
            ? formatCurrency(p.rate, displayCurrency as string)
            : undefined,
        },
        iconComponent: () => <CurrencyIcon currencyCode={p.code} />,
        percentageChange: (
          <ExploreGainLoss
            productCode={p.code}
            productType={p.type}
            productGraph={productGraph}
            isTracked={p.isTracked}
          />
        ),
        action: (
          <div className="flex justify-end">
            <ExploreActionButton
              productCode={p.code}
              productType={p.type}
              isTracked={p.isTracked}
              isMobile={isMobile}
            />
          </div>
        ),
      };
    }) ?? [];

  return (
    <div className={'px-2 pb-2'}>
      <ListContainer>
        {!isMobile && combinedData.length && (
          <ProductListHeader
            className="p-2 pr-4"
            assetHeader={localizedContent.asset}
            balanceHeader={localizedContent.lastPrice}
            percentageChangeHeader={localizedContent.percentageChange}
          />
        )}
        {combinedData.map((p, index) => (
          <ProductListItem
            key={p.code}
            assetColumn={
              <div className="flex items-center">
                <div className="flex-column justify-center">
                  <div className="flex items-center">
                    <Typography variant="callToAction" className="text-white">
                      {p.title}
                    </Typography>
                    {p.tag && (
                      <GradientOrColorView
                        key={p.tag.code}
                        colorOrGradient={getColorOrGradient(p.tag.colors)}
                        style={{
                          color: p.tag.colors.text,
                        }}
                        className="rounded-full ml-2 px-1 py-0.5 leading-tight text-xs font-normal h-fit">
                        {p.tag.displayName}
                      </GradientOrColorView>
                    )}
                  </div>

                  <Typography size="small" className="text-neutral-300">
                    {p.subtitle}
                  </Typography>
                </div>
              </div>
            }
            actionsColumn={p.action}
            priceColumn={
              <>
                <div className="flex items-end justify-center">
                  <Typography size="medium" className="text-white">
                    {p.balanceText?.primary}
                  </Typography>
                </div>
              </>
            }
            percentageChangeColumn={p.percentageChange}
            {...p}
            onPress={
              p.isTracked
                ? () => navigate({ to: `/products/${p.code}` })
                : undefined
            }
            className={clsx(
              'px-4',
              index === 0 && 'rounded-tr-2xl rounded-tl-2xl',
              index === combinedData.length - 1 &&
                'rounded-br-2xl rounded-bl-2xl',
            )}
            isMobile={isMobile}
            showChevron={!isMobile && p.isTracked}
          />
        ))}
      </ListContainer>
      {isLoading && (
        <div className="pb-8">
          <CustomLoader />
        </div>
      )}
      {isSearching && combinedData.length === 0 && <NoProductsFound />}
    </div>
  );
};

export default ProductsTable;
